import React, { useState, useCallback, useEffect, useRef } from 'react';
import { fetchProxiedIcs } from '../lib/api';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { motion, AnimatePresence } from 'motion/react';
import { collection, addDoc, writeBatch, doc, getDoc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { track } from '../lib/analytics';
import { parseGCCsv, parseGCFilename } from '../lib/gcCsvParser';
import { PlayerStats } from '../lib/xovr-engine';
import { calculateOVR, suggestAttributesFromStats, derivePositionsFromFieldTime } from '../lib/utils';
import { PITCH_COUNT_RULESETS } from '../lib/pitch-count-rules';
import { celebrate } from '../lib/haptics';
import { FileUp, ChevronLeft, Check, AlertTriangle, Trash2, ChevronDown, Users, KeyRound, History, Calendar as CalendarIcon, ShieldCheck, X } from 'lucide-react';
import { cn } from '../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

type StyleOfPlay = 'tballcoachpitch' | 'machinepitch' | 'playerspitch';
type Step = 'csv' | 'statsmode' | 'details' | 'style' | 'agegroup' | 'orglevel' | 'calendar' | 'roster';

// 'currentSeason': CSV stats are this season's running totals — live tracking.
// 'priorBaseline': CSV stats are from a previous season — context only, won't
//                  mix with new game stats.
type StatsMode = 'currentSeason' | 'priorBaseline';

interface RosterPlayer {
  id: string;
  name: string;
  number: string;
  stats: PlayerStats | null;
  fieldTime: Record<string, number> | null;
  attributes: ReturnType<typeof suggestAttributesFromStats>;
  primaryPosition: string;
  secondaryPosition: string;
  positions: Record<string, number>;
  isPitcher: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AGE_GROUPS = [
  '5U', '6U', '7U', '8U', '9U', '10U', '11U', '12U',
  '13U', '14U', '15U-18U', 'HS JV', 'HS Varsity',
];

const ORGS = [
  { value: 'usssa',        label: 'USSSA',         levels: ['A', 'AA', 'AAA', 'Major', 'Futures'] },
  { value: 'perfectgame',  label: 'Perfect Game',  levels: ['Open', 'Premier', 'Elite'] },
  { value: 'littleleague', label: 'Little League', levels: ['Minor', 'Major', 'Junior', 'Senior'] },
  { value: 'nfhs',         label: 'NFHS',          levels: ['JV', 'Varsity'] },
  { value: 'independent',  label: 'Independent',   levels: ['Rec', 'Competitive', 'Travel'] },
];

const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];

const STYLE_OPTIONS = [
  { value: 'tballcoachpitch'  as StyleOfPlay, label: 'T-Ball/Coach Pitch', desc: 'Fairness rotations, full game planner' },
  { value: 'machinepitch'as StyleOfPlay, label: 'Machine Pitch',      desc: 'Full fielding rotations, complete scouting matrix' },
  { value: 'playerspitch'as StyleOfPlay, label: 'Players Pitch',       desc: 'Full engine: pitch counts, xOVR, reliever intel' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

// Normalize the wizard's raw org keys (UI uses 'littleleague' etc. for
// historical reasons) to the PITCH_COUNT_RULESETS keys ('little_league' etc.).
// Used by handleCreate when writing to Firestore AND by LeagueRulesConfirmSheet
// when looking up the ruleset for display — keep them in sync.
function normalizeOrg(o: string): string {
  if (o === 'littleleague') return 'little_league';
  if (o === 'perfectgame')  return 'perfect_game';
  return o;
}

// Map the wizard's age group strings (e.g. '12U', 'HS Varsity') to the keys
// used inside each ruleset's ageGroups map. Most match directly, but NFHS
// uses 'all' for everything, and a few wizard age groups have no ruleset row.
function resolveAgeGroupKey(rulesetId: string, ageGroup: string): string | null {
  const ruleset = PITCH_COUNT_RULESETS[rulesetId];
  if (!ruleset) return null;
  const keys = Object.keys(ruleset.ageGroups);
  if (keys.length === 0) return null;
  // NFHS pattern: single 'all' bucket
  if (keys.length === 1 && keys[0] === 'all') return 'all';
  // Direct match
  if (keys.includes(ageGroup)) return ageGroup;
  // Wizard-only labels (HS JV / HS Varsity / 15U-18U) fall back to the
  // highest age row the ruleset has — best-effort approximation.
  if (ageGroup === 'HS JV' || ageGroup === 'HS Varsity' || ageGroup === '15U-18U') {
    const numericAges = keys.filter(k => /^\d+U$/.test(k));
    if (numericAges.length > 0) {
      return numericAges.sort((a, b) => parseInt(b) - parseInt(a))[0];
    }
  }
  return null;
}

function defaultAttributes() {
  return {
    attributes: {
      hitting:     { contact: 3, power: 3, eye: 3, approach: 3 },
      fielding:    { glove: 3, arm: 3, block: 3 },
      intangibles: { speed: 3, iq: 3, clutch: 3 },
      pitching:    { velocity: 0, control: 0 },
    },
    positions:         { P: 0, C: 0, '1B': 0, '2B': 0, '3B': 0, SS: 0, LF: 0, CF: 0, RF: 0 },
    primaryPosition:   '',
    secondaryPosition: '',
  };
}

// Inline ICS helpers — same logic as ChecklistCalendarSync.tsx and
// CalendarImporter.tsx. Duplicated for now; if a fourth place needs this it
// should move to src/lib/icsParser.ts.
function parseICS(text: string, appTeamName: string) {
  const games: any[] = [];
  const vevents = text.split('BEGIN:VEVENT');
  vevents.shift();
  const rawEvents: { summary: string; dtstart: string }[] = [];
  vevents.forEach(event => {
    const summary = event.match(/SUMMARY:(.*)/)?.[1] || 'Unknown Game';
    if (summary.toLowerCase().includes('practice')) return;
    const dtstart = event.match(/DTSTART[:;](?:VALUE=DATE:)?(\d{8}T\d{6}Z?|\d{8})/)?.[1];
    if (dtstart) rawEvents.push({ summary, dtstart });
  });
  const teamTally: Record<string, number> = {};
  const extract = (s: string) => {
    const m = s.match(/(.*?)(?:\s+at\s+|\s+@\s+|\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);
    return m ? [m[1].trim(), m[2].trim()] : null;
  };
  rawEvents.forEach(e => {
    const t = extract(e.summary);
    if (t) { teamTally[t[0]] = (teamTally[t[0]] || 0) + 1; teamTally[t[1]] = (teamTally[t[1]] || 0) + 1; }
  });
  let userIcsTeam = '';
  const appLower = appTeamName.toLowerCase();
  for (const t of Object.keys(teamTally)) {
    if (t.toLowerCase().includes(appLower) || appLower.includes(t.toLowerCase())) { userIcsTeam = t; break; }
  }
  if (!userIcsTeam) {
    let max = 0;
    for (const [t, c] of Object.entries(teamTally)) { if (c > max) { max = c; userIcsTeam = t; } }
  }
  rawEvents.forEach(e => {
    let opponent = e.summary; let isHome = false;
    const mAt = e.summary.match(/(.*?)(?:\s+at\s+|\s+@\s+)(.*)/i);
    const mVs = e.summary.match(/(.*?)(?:\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);
    if (mAt) {
      if (mAt[2].trim() === userIcsTeam) { isHome = true; opponent = mAt[1].trim(); }
      else { opponent = mAt[2].trim(); }
    } else if (mVs) {
      if (mVs[1].trim() === userIcsTeam) { isHome = true; opponent = mVs[2].trim(); }
      else { opponent = mVs[2].trim(); }
    } else {
      opponent = e.summary.replace(/^Game vs\s+/i, '').replace(/^vs\.?\s+/i, '').trim();
    }
    const yr = e.dtstart.substring(0, 4), mo = e.dtstart.substring(4, 6), dy = e.dtstart.substring(6, 8);
    const hr = e.dtstart.substring(9, 11) || '00', mn = e.dtstart.substring(11, 13) || '00';
    games.push({ opponent, isHome, date: new Date(`${yr}-${mo}-${dy}T${hr}:${mn}:00Z`).toISOString(), status: 'scheduled', createdAt: new Date().toISOString() });
  });
  return games;
}

// ─── Step: CSV Drop ───────────────────────────────────────────────────────────

const CsvStep = ({
  csvParsed, csvParsing, csvError, rosterCount, pitcherCount, teamName, season, seasonYear, hasStats,
  getRootProps, getInputProps, isDragActive,
  onNext, onSkip,
}: {
  csvParsed: boolean; csvParsing: boolean; csvError: string | null;
  rosterCount: number; pitcherCount: number; teamName: string; season: string; seasonYear: number; hasStats: boolean;
  getRootProps: any; getInputProps: any; isDragActive: boolean;
  onNext: () => void; onSkip: () => void;
}) => (
  <div className="p-6 max-w-md mx-auto space-y-8 pt-10">
    <div className="space-y-3">
      <h1 className="text-4xl font-black italic tracking-tight uppercase leading-none">
        One File.<br />Whole Team.
      </h1>
      <p className="text-zinc-400 text-sm leading-relaxed">
        Drop your GameChanger export and Dugout builds the whole thing &mdash; team name, season, your full roster with numbers, and every stat. No typing a roster by hand.
      </p>
      <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">
        In GameChanger: Stats → Season → Export → CSV
      </p>
    </div>

    {!csvParsed ? (
      <>
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-3xl p-12 flex flex-col items-center justify-center space-y-4 cursor-pointer transition-all',
            csvParsing
              ? 'border-emerald-500/40 bg-emerald-500/5 cursor-wait'
              : isDragActive
                ? 'border-emerald-500/60 bg-emerald-500/5'
                : 'border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/30'
          )}
        >
          <input {...getInputProps()} />
          <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center">
            {csvParsing
              ? <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              : <FileUp className="w-8 h-8 text-zinc-500" />
            }
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-bold text-white">
              {csvParsing ? 'Building your team...' : isDragActive ? 'Drop it' : 'Drop your CSV here'}
            </p>
            {!csvParsing && (
              <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">or tap to browse</p>
            )}
          </div>
        </div>

        {csvError && (
          <div className="flex items-start space-x-3 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-400">{csvError}</p>
          </div>
        )}

        {/* Manual setup as an equal, respectable choice — not a gray afterthought. */}
        <button
          onClick={onSkip}
          className="w-full py-4 bg-zinc-900 border border-zinc-800 rounded-2xl text-sm font-bold text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
        >
          No GameChanger? Set up manually →
        </button>
      </>
    ) : (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="space-y-6"
      >
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-3xl p-8 flex flex-col items-center space-y-5 text-center">
          <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.3)]">
            <Check className="w-8 h-8 text-black" />
          </div>
          <div className="space-y-1">
            {teamName && <p className="text-xl font-black italic text-white leading-tight">{teamName}</p>}
            {(season || seasonYear) && (
              <p className="text-[11px] font-bold text-emerald-300/80 uppercase tracking-widest">
                {[season, seasonYear].filter(Boolean).join(' ')}
              </p>
            )}
          </div>

          {/* What the one file actually delivered */}
          <div className="w-full grid grid-cols-3 gap-2 pt-1">
            {[
              { value: rosterCount, label: 'Players' },
              { value: pitcherCount, label: 'Pitchers' },
              { value: hasStats ? 'Yes' : '—', label: 'Stats' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-black/20 rounded-xl py-3">
                <div className="text-2xl font-black italic text-white leading-none">{value}</div>
                <div className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-1">{label}</div>
              </div>
            ))}
          </div>

          <p className="text-sm text-zinc-400">
            Built from one file. {hasStats ? 'xOVR is ready to fire.' : 'Add ratings next to activate xOVR.'}
          </p>
        </div>
        <button
          onClick={onNext}
          className="w-full py-4 bg-emerald-500 text-black font-black rounded-2xl uppercase italic text-lg shadow-[0_0_30px_rgba(16,185,129,0.2)]"
        >
          Let's Go →
        </button>
      </motion.div>
    )}
  </div>
);

// ─── Step: Stats Mode (current season vs prior baseline) ────────────────────
// Only shown when the coach actually uploaded a CSV with usable stats. The
// answer routes stats to either `player.stats` (live tracking) or the new
// `player.priorSeasonStats` baseline field.

const StatsModeStep = ({
  statsMode, setStatsMode, parsedSeason, parsedYear, playerCount,
}: {
  statsMode: StatsMode;
  setStatsMode: (m: StatsMode) => void;
  parsedSeason: string;
  parsedYear: number;
  playerCount: number;
}) => (
  <div className="p-6 max-w-md mx-auto space-y-7 pt-10">
    <div className="space-y-3">
      <h2 className="text-3xl font-black italic uppercase">About these stats</h2>
      <p className="text-zinc-400 text-sm leading-relaxed">
        You imported {playerCount} {playerCount === 1 ? 'player' : 'players'} with stats from <span className="font-bold text-white">{parsedSeason} {parsedYear}</span>. Are these your current season totals, or a prior season for context?
      </p>
    </div>

    <div className="space-y-3">
      <button
        onClick={() => setStatsMode('currentSeason')}
        className={cn(
          'w-full text-left p-5 rounded-2xl border-2 transition-all',
          statsMode === 'currentSeason'
            ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.08)]'
            : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
        )}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
            <CalendarIcon className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-base font-black uppercase">Current Season</span>
              {statsMode === 'currentSeason' && (
                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              I'm joining mid-season. Track these as my live season totals. New game stats will add to them.
            </p>
          </div>
        </div>
      </button>

      <button
        onClick={() => setStatsMode('priorBaseline')}
        className={cn(
          'w-full text-left p-5 rounded-2xl border-2 transition-all',
          statsMode === 'priorBaseline'
            ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.08)]'
            : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
        )}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
            <History className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-base font-black uppercase">Prior Season Baseline</span>
              {statsMode === 'priorBaseline' && (
                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              These are from last season. Use them as a starting reference for xOVR. They won't mix with new game stats.
            </p>
          </div>
        </div>
      </button>
    </div>

    <p className="text-[10px] text-zinc-600 leading-relaxed">
      Either way, GameChanger position history will be used to set each player's starting positions.
    </p>
  </div>
);

// ─── Step: Team Details ───────────────────────────────────────────────────────

const DetailsStep = ({
  teamName, setTeamName, season, setSeason, seasonYear, setSeasonYear,
  showRecord, recordW, recordL, recordT, setRecordW, setRecordL, setRecordT,
}: {
  teamName: string; setTeamName: (v: string) => void;
  season: string; setSeason: (v: string) => void;
  seasonYear: number; setSeasonYear: (v: number) => void;
  // Mid-season record block. Only rendered when the coach said the imported
  // CSV stats are for the current season — GC stats don't include W-L-T.
  showRecord: boolean;
  recordW: number; setRecordW: (v: number) => void;
  recordL: number; setRecordL: (v: number) => void;
  recordT: number; setRecordT: (v: number) => void;
}) => {
  const thisYear = new Date().getFullYear();
  return (
    <div className="p-6 max-w-md mx-auto space-y-8 pt-10 pb-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-black italic uppercase">Your Team</h2>
        <p className="text-zinc-400 text-sm">Confirm your details. We pre-filled what we could from the file.</p>
      </div>

      <div className="space-y-5">
        <div className="space-y-2">
          <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Team Name</label>
          <input
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            placeholder="e.g. White Sox"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3.5 text-white font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Season</label>
          <div className="grid grid-cols-4 gap-2">
            {SEASONS.map(s => (
              <button
                key={s}
                onClick={() => setSeason(s)}
                className={cn(
                  'py-3 rounded-xl text-sm font-black uppercase transition-all',
                  season === s
                    ? 'bg-emerald-500 text-black shadow-[0_0_16px_rgba(16,185,129,0.2)]'
                    : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Year</label>
          <select
            value={seasonYear}
            onChange={e => setSeasonYear(Number(e.target.value))}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3.5 text-white font-black focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
          >
            {Array.from({ length: 7 }, (_, i) => thisYear - 1 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Mid-season record — only shown when the imported CSV stats are
            for the current season. GC CSVs don't include team W-L-T, so we
            ask the coach directly. Future game results add to this baseline. */}
        {showRecord && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2 pt-2 border-t border-zinc-900"
          >
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Current Record</label>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              GameChanger stats don't include W-L-T. Set your team's record so far this season, for games you won't enter in Dugout. Games you log here are counted on top of it, so don't include those.
            </p>
            <div className="grid grid-cols-3 gap-2 pt-1">
              {([
                { label: 'Wins', value: recordW, set: setRecordW, accent: 'text-emerald-400' },
                { label: 'Losses', value: recordL, set: setRecordL, accent: 'text-red-400' },
                { label: 'Ties', value: recordT, set: setRecordT, accent: 'text-zinc-300' },
              ] as const).map(({ label, value, set, accent }) => (
                <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex flex-col items-center gap-2">
                  <span className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">{label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => set(Math.max(0, value - 1))}
                      className="w-7 h-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-black text-sm transition-colors"
                      aria-label={`Decrease ${label}`}
                    >−</button>
                    <span className={cn('text-2xl font-black tabular-nums w-8 text-center', accent)}>{value}</span>
                    <button
                      type="button"
                      onClick={() => set(value + 1)}
                      className="w-7 h-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-black text-sm transition-colors"
                      aria-label={`Increase ${label}`}
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ─── Step: Style of Play ──────────────────────────────────────────────────────

const StyleStep = ({
  styleOfPlay, setStyleOfPlay, strategyFocus, setStrategyFocus
}: {
  styleOfPlay: StyleOfPlay | ''; setStyleOfPlay: (v: StyleOfPlay) => void;
  strategyFocus: 'fairness' | 'scouting'; setStrategyFocus: (v: 'fairness' | 'scouting') => void;
}) => {
  // Primary Objective sits below the fold on most viewports. When a style is
  // chosen, scroll it into view so the coach knows the next decision exists —
  // makes the wizard feel like it's guiding them rather than hiding things.
  const objectiveRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (styleOfPlay === '' || !objectiveRef.current) return;
    objectiveRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [styleOfPlay]);

  return (
    <div className="p-6 max-w-md mx-auto space-y-8 pt-10">
      <div className="space-y-2">
        <h2 className="text-3xl font-black italic uppercase">Style of Play</h2>
        <p className="text-zinc-400 text-sm">This unlocks the right engines for your team.</p>
      </div>

      <div className="space-y-3">
        {STYLE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setStyleOfPlay(opt.value)}
            className={cn(
              'w-full text-left p-5 rounded-2xl border-2 transition-all',
              styleOfPlay === opt.value
                ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.08)]'
                : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700'
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-base font-black uppercase">{opt.label}</span>
              {styleOfPlay === opt.value && (
                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{opt.desc}</p>
          </button>
        ))}
      </div>

      {styleOfPlay !== '' && (
        <motion.div
          ref={objectiveRef}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 border-t border-zinc-900 space-y-4"
        >
          <div className="space-y-1">
            <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Primary Objective</h3>
            <p className="text-xs text-zinc-400">How should Dugout build your lineups?</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setStrategyFocus('fairness')}
              className={cn(
                "p-4 rounded-xl border-2 text-left transition-all",
                strategyFocus === 'fairness' ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900"
              )}
            >
              <div className="font-black text-[10px] uppercase mb-1">Fairness</div>
              <div className="text-[9px] text-zinc-500 leading-tight">Equal field time for every kid.</div>
            </button>
            <button
              onClick={() => setStrategyFocus('scouting')}
              className={cn(
                "p-4 rounded-xl border-2 text-left transition-all",
                strategyFocus === 'scouting' ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-800 bg-zinc-900"
              )}
            >
              <div className="font-black text-[10px] uppercase mb-1">Competitive</div>
              <div className="text-[9px] text-zinc-500 leading-tight">Best fielders at key positions.</div>
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 leading-relaxed italic">
            These are your defaults. You can change them any time in Settings.
          </p>
        </motion.div>
      )}
    </div>
  );
};

// ─── Step: Age Group ──────────────────────────────────────────────────────────

const AgeGroupStep = ({
  ageGroup, setAgeGroup,
}: {
  ageGroup: string; setAgeGroup: (v: string) => void;
}) => (
  <div className="p-6 max-w-md mx-auto space-y-8 pt-10">
    <div className="space-y-2">
      <h2 className="text-3xl font-black italic uppercase">Age Group</h2>
      <p className="text-zinc-400 text-sm">Select your team's age group. This drives pitch count rules and sets the context for your team's stats.</p>
    </div>

    <div className="grid grid-cols-4 gap-2">
      {AGE_GROUPS.map(ag => (
        <button
          key={ag}
          onClick={() => setAgeGroup(ag)}
          className={cn(
            'py-3 rounded-xl text-xs font-black uppercase transition-all',
            ageGroup === ag
              ? 'bg-emerald-500 text-black shadow-[0_0_12px_rgba(16,185,129,0.2)]'
              : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
          )}
        >
          {ag}
        </button>
      ))}
    </div>
  </div>
);

// ─── Step: Org + Level ────────────────────────────────────────────────────────

const OrgLevelStep = ({
  org, setOrg, level, setLevel, ageGroup, onConfirmRules,
}: {
  org: string; setOrg: (v: string) => void;
  level: string; setLevel: (v: string) => void;
  ageGroup: string;
  onConfirmRules: () => void;
}) => {
  const selectedOrg = ORGS.find(o => o.value === org) ?? ORGS[0];

  // Always-visible summary of the actual rule that will apply, so a coach who
  // trusts the defaults still sees the consequence without opening the sheet.
  const rulesetId = normalizeOrg(org);
  const ruleset = PITCH_COUNT_RULESETS[rulesetId];
  const ageKey = resolveAgeGroupKey(rulesetId, ageGroup);
  const dailyMax = ageKey && ruleset ? ruleset.ageGroups[ageKey]?.dailyMax : null;

  return (
    <div className="p-6 max-w-md mx-auto space-y-7 pt-10 pb-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-black italic uppercase">League</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Pick the league or tournament org you play in most. This sets your default pitch count rules.
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Organization</label>
          <div className="space-y-2">
            {ORGS.map(o => (
              <button
                key={o.value}
                onClick={() => { setOrg(o.value); setLevel(o.levels[0]); }}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all',
                  org === o.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700'
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Level</label>
          <div className="flex flex-wrap gap-2">
            {selectedOrg.levels.map(l => (
              <button
                key={l}
                onClick={() => setLevel(l)}
                className={cn(
                  'px-5 py-2.5 rounded-xl text-sm font-black uppercase transition-all',
                  level === l
                    ? 'bg-emerald-500 text-black shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                    : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tournament reassurance — coaches who play dual formats often stall
          here wondering if Dugout can handle a different ruleset for
          tournaments. It can — Tournament Mode lets you set per-event rules. */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-start gap-2.5">
        <CalendarIcon className="w-3.5 h-3.5 text-zinc-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          Tournament games can use their own rules. You'll choose those when you create a tournament.
        </p>
      </div>

      {/* Always-visible consequence of the current selection — so a coach who
          trusts the defaults still sees the actual limit before moving on. */}
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-2.5">
        <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
        <p className="text-[12px] text-zinc-200 leading-snug">
          {dailyMax != null ? (
            <><strong className="text-white">{ageGroup} {selectedOrg.label}</strong>: Dugout will enforce a <strong className="text-white">{dailyMax} pitch</strong> daily max, plus rest days.</>
          ) : (
            <><strong className="text-white">{ageGroup} {selectedOrg.label}</strong>: pitch limits will follow this league's rules.</>
          )}
        </p>
      </div>

      {/* Fork: most coaches will trust the defaults and tap the green Next
          in the footer. The cautious coach can tap this to see exactly which
          rules Dugout will apply for their org/level/age group. */}
      <button
        onClick={onConfirmRules}
        className="w-full py-3 rounded-2xl border-2 border-zinc-700 hover:border-zinc-600 bg-transparent text-zinc-300 font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
      >
        <ShieldCheck className="w-4 h-4" />
        See full rule details
      </button>
    </div>
  );
};

// ─── Sheet: Confirm League Rules ──────────────────────────────────────────────
// Modal sheet shown over the orglevel step when the coach taps "Manually
// Confirm League Rules". Read-only — no toggles in here, by design. The goal
// is transparency: show the coach exactly what Dugout will enforce. Editing
// belongs in Settings.

const LeagueRulesConfirmSheet = ({
  org, level, ageGroup, onClose, onContinue,
}: {
  org: string;       // Wizard's raw org value (e.g., 'littleleague')
  level: string;     // Selected level chip (e.g., 'Major')
  ageGroup: string;  // Wizard's age group (e.g., '12U' or 'HS Varsity')
  onClose: () => void;
  onContinue: () => void;
}) => {
  const normalizedOrg = normalizeOrg(org);
  const ruleset = PITCH_COUNT_RULESETS[normalizedOrg];
  const ageKey = ruleset ? resolveAgeGroupKey(normalizedOrg, ageGroup) : null;
  const ageRules: any = ruleset && ageKey ? ruleset.ageGroups[ageKey] : null;

  // Compose the compact summary line — same pattern as SettingsView.
  const primaryLimit = ageRules
    ? (ageRules.logicType === 'innings' ? `${ageRules.dailyMaxInnings} innings/day`
      : ageRules.logicType === 'outs'    ? `${ageRules.dailyMaxOuts} outs/day`
      : `${ageRules.dailyMax} pitches/day`)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-zinc-950/90 backdrop-blur-xl flex items-end sm:items-center justify-center p-0 sm:p-6"
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-zinc-950 sm:bg-zinc-900 w-full sm:max-w-md sm:rounded-3xl border-t sm:border border-zinc-800 max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between border-b border-zinc-900 shrink-0">
          <div>
            <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Confirm Your League Rules</p>
            <h2 className="text-lg font-black italic uppercase tracking-tight">Here's the rules we'll alert you on</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Leading honesty sentence — sets expectation that the rules below
              are advisory (we track + alert), and points coaches to Settings
              for the toggleable hard-block options at the bottom. */}
          <p className="text-xs text-zinc-400 leading-relaxed">
            Dugout tracks every pitch and alerts you on rest and restrictions. You can set more granular permissions in Settings.
          </p>

          {/* Summary card — reuses SettingsView's compact pattern. Description
              text uses neutral zinc (not emerald-on-emerald) for contrast. */}
          {ruleset && ageRules ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 space-y-2">
              <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">
                {ruleset.name} · {ageKey} · {primaryLimit}
                {level && level !== 'AA' && level !== ageKey && (
                  <span className="text-zinc-500 font-bold normal-case tracking-normal"> · {level}</span>
                )}
              </p>
              <p className="text-[11px] text-zinc-300 leading-relaxed">
                {ruleset.description}
              </p>
            </div>
          ) : (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
              <p className="text-xs text-zinc-400">
                We don't have a built-in ruleset for {org}{ageGroup ? ` · ${ageGroup}` : ''}. You can define your own rules from Settings after onboarding.
              </p>
            </div>
          )}

          {/* Rest thresholds (Little League / NFHS) */}
          {ageRules && ageRules.logicType === 'threshold' && ageRules.restThresholds && ageRules.restThresholds.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Rest Required After</p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl divide-y divide-zinc-800 overflow-hidden">
                {ageRules.restThresholds.map((t: any, i: number) => (
                  <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">
                      {t.minPitches}–{t.maxPitches} pitches
                    </span>
                    <span className="text-xs font-black text-emerald-400">
                      {t.daysRestRequired === 0 ? 'No rest' : `${t.daysRestRequired} day${t.daysRestRequired === 1 ? '' : 's'} rest`}
                    </span>
                  </div>
                ))}
              </div>
              {ageRules.rollingWindowDays && ageRules.rollingWindowMaxPitches && (
                <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
                  Plus a rolling cap: max {ageRules.rollingWindowMaxPitches} pitches in any {ageRules.rollingWindowDays}-day window.
                </p>
              )}
              {ageRules.noConsecutiveDays && (
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  No pitching on 3 consecutive calendar days.
                </p>
              )}
            </div>
          )}

          {/* Innings limits (USSSA) */}
          {ageRules && ageRules.logicType === 'innings' && (
            <div className="space-y-2">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Innings Limits</p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl divide-y divide-zinc-800 overflow-hidden">
                {ageRules.dailyMaxInnings && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Max per day</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.dailyMaxInnings} innings</span>
                  </div>
                )}
                {ageRules.eventMaxInnings && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Max per event</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.eventMaxInnings} innings</span>
                  </div>
                )}
                {ageRules.nextDayBlockInnings && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Next-day block</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.nextDayBlockInnings}+ innings</span>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
                One pitch in an inning counts as a full inning. {ageRules.nextDayBlockInnings ? `Throwing ${ageRules.nextDayBlockInnings}+ innings blocks the pitcher the next day.` : ''}
              </p>
            </div>
          )}

          {/* Outs limits (Perfect Game) */}
          {ageRules && ageRules.logicType === 'outs' && (
            <div className="space-y-2">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Outs & Pitch Limits</p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl divide-y divide-zinc-800 overflow-hidden">
                {ageRules.dailyMaxOuts && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Max outs per day</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.dailyMaxOuts} outs</span>
                  </div>
                )}
                {ageRules.dailyMaxPitches && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Max pitches per day</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.dailyMaxPitches} pitches</span>
                  </div>
                )}
                {ageRules.rollingOutsWindowDays && ageRules.rollingOutsWindowMax && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Rolling {ageRules.rollingOutsWindowDays}-day cap</span>
                    <span className="text-xs font-black text-emerald-400">{ageRules.rollingOutsWindowMax} outs</span>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
                Whichever limit hits first, outs or pitches, removes the pitcher.
              </p>
            </div>
          )}

          {/* Tournament reassurance — placed right after the pitch limits
              detail (rest thresholds / innings / outs). Scoped specifically to
              pitch limits since catcher/pitcher position rules below do NOT
              vary by tournament — only pitch counts do. */}
          {ageRules && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                <span className="font-bold text-zinc-300">These pitch limits apply to league games.</span> Tournaments use their own pitch counts. Set those when you create a tournament.
              </p>
            </div>
          )}

          {/* Optional hard blocks — these four CAN be hard blocks when their
              individual toggles are enabled in Settings → Pitching. They're
              not unconditional; the coach controls each. Be explicit about
              that so the section doesn't overpromise. */}
          <div className="pt-3 border-t border-zinc-900 space-y-3">
            <div className="space-y-1">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest">Position blocks (when toggled on)</p>
              <p className="text-[10px] text-zinc-500 leading-relaxed">
                Each of these is a Settings toggle. When the toggle is on, Dugout will hard-block the assignment. Otherwise the coach decides.
              </p>
            </div>
            <ul className="space-y-1.5 text-[11px] text-zinc-300 leading-relaxed">
              <li className="flex gap-2"><span className="text-amber-400 shrink-0">•</span><span>No pitcher re-entry once they leave the game.</span></li>
              <li className="flex gap-2"><span className="text-amber-400 shrink-0">•</span><span>A catcher in this game can't pitch later in the same game.</span></li>
              <li className="flex gap-2"><span className="text-amber-400 shrink-0">•</span><span>A pitcher in this game can't catch later in the same game.</span></li>
              <li className="flex gap-2"><span className="text-amber-400 shrink-0">•</span><span>Once a pitcher crosses a high pitch threshold, they can't return as catcher.</span></li>
            </ul>
            <p className="text-[10px] text-zinc-600 leading-relaxed italic">
              Toggle any of these on or off in Settings → Pitching.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-zinc-900 shrink-0 space-y-2">
          <button
            onClick={onContinue}
            className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-black rounded-2xl uppercase italic transition-colors shadow-[0_0_20px_rgba(16,185,129,0.2)]"
          >
            Looks Good, Continue →
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-xs font-bold text-zinc-500 hover:text-zinc-300 uppercase tracking-widest transition-colors"
          >
            Back to League Selection
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Step: Calendar Sync ──────────────────────────────────────────────────────
// Optional, but offered to everyone since calendar sync is free for all tiers.
// The actual ICS fetch + write happens in handleCreate after the team doc
// exists so games can be written into the team subcollection.

const CalendarStep = ({
  icsUrl, setIcsUrl,
}: {
  icsUrl: string;
  setIcsUrl: (v: string) => void;
}) => (
  <div className="p-6 max-w-md mx-auto space-y-7 pt-10">
    <div className="space-y-3">
      <h2 className="text-3xl font-black italic uppercase">Sync your schedule</h2>
      <p className="text-zinc-400 text-sm leading-relaxed">
        Paste your GameChanger calendar link and we'll pull in your full season. You can skip this and add it later from Settings.
      </p>
    </div>

    {/* Step-by-step GC instructions */}
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-2">
      <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3">How to get your link</p>
      {[
        'Open GameChanger',
        'Tap Team → Team Settings',
        'Tap Schedule Sync',
        'Tap Sync Schedule to your Calendar',
        'Tap Copy Calendar Link',
      ].map((step, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="w-5 h-5 bg-zinc-800 rounded-full flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-[9px] font-black text-zinc-400">{i + 1}</span>
          </div>
          <span className="text-xs text-zinc-400">{step}</span>
        </div>
      ))}
    </div>

    {/* Paste field */}
    <div className="space-y-2">
      <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Paste your calendar link</label>
      <input
        type="url"
        value={icsUrl}
        onChange={e => setIcsUrl(e.target.value)}
        placeholder="webcal://…"
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500/50 transition-colors placeholder:text-zinc-700"
      />
      {icsUrl.trim().length > 0 && (
        <p className="text-[11px] text-emerald-400 font-bold">We'll import your games when you create your team.</p>
      )}
    </div>

    <p className="text-[10px] text-zinc-600 leading-relaxed">
      You can always sync later from Settings or the Games tab.
    </p>
  </div>
);

// ─── Step: Roster Preview ─────────────────────────────────────────────────────

const POSITION_OPTIONS = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];

const RosterStep = ({
  roster, onAddPlayer, onRemove, onUpdateAttributes, onUpdatePositions,
  statsMode, parsedSeason, parsedYear, hasUsableStats, onImportInstead,
}: {
  roster: RosterPlayer[];
  onAddPlayer: (player: RosterPlayer) => void;
  onRemove: (id: string) => void;
  onUpdateAttributes: (id: string, attributes: any) => void;
  onUpdatePositions: (id: string, primary: string, secondary: string) => void;
  statsMode: StatsMode;
  parsedSeason: string;
  parsedYear: number;
  hasUsableStats: boolean;
  onImportInstead: () => void;
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const pitcherCount = roster.filter(p => p.isPitcher).length;
  const positionedCount = roster.filter(p => p.primaryPosition).length;

  // Manual add-player form state. This is the path for coaches who don't use
  // GameChanger (or skipped the CSV step) — without it they'd finish onboarding
  // with an empty team. Players are seeded with neutral default attributes; the
  // coach refines them later in the Scouting Session.
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newPosition, setNewPosition] = useState('');

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    const base = defaultAttributes();
    onAddPlayer({
      id: makeId(),
      name,
      number: newNumber.trim(),
      stats: null,
      fieldTime: null,
      attributes: base,
      primaryPosition: newPosition,
      secondaryPosition: '',
      positions: base.positions,
      isPitcher: newPosition === 'P',
    });
    setNewName('');
    setNewNumber('');
    setNewPosition('');
  };

  return (
    <div className="p-6 max-w-md mx-auto space-y-5 pt-10 pb-36">
      <div className="space-y-2">
        <h2 className="text-3xl font-black italic uppercase">
          {roster.length > 0 ? "Here's what we found" : 'Build your roster'}
        </h2>
        <p className="text-zinc-400 text-sm">
          {roster.length > 0
            ? `${roster.length} players imported${positionedCount > 0 ? ` · ${positionedCount} with positions` : ''}${pitcherCount > 0 ? ` · ${pitcherCount} ${pitcherCount === 1 ? 'pitcher' : 'pitchers'}` : ''}.`
            : 'Add your players below. You can fine-tune ratings and positions anytime, and scout them in detail after setup.'}
        </p>
      </div>

      {/* Manual add-player — always available so a coach can add a kid the CSV
          missed, and the only way to build a roster when there's no CSV. */}
      <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4 space-y-3">
        <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Add a Player</p>
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Player name"
            className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm font-bold outline-none focus:border-emerald-500 transition-colors placeholder:text-zinc-600"
          />
          <input
            value={newNumber}
            onChange={e => setNewNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="#"
            inputMode="numeric"
            className="w-14 shrink-0 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-2.5 text-sm font-bold text-center outline-none focus:border-emerald-500 transition-colors placeholder:text-zinc-600"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={newPosition}
            onChange={e => setNewPosition(e.target.value)}
            className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm font-bold outline-none focus:border-emerald-500 transition-colors"
          >
            <option value="">Position (optional)</option>
            {POSITION_OPTIONS.map(pos => <option key={pos} value={pos}>{pos}</option>)}
          </select>
          <button
            onClick={handleAdd}
            disabled={!newName.trim()}
            className="px-6 shrink-0 bg-emerald-500 text-black font-black rounded-lg uppercase text-xs tracking-widest disabled:opacity-40 transition-opacity"
          >
            Add
          </button>
        </div>
      </div>

      {/* Escape hatch back to CSV import — typing players by hand gets old fast;
          this is for the coach who realizes partway through that they have a
          GameChanger export sitting on their phone. Clears the manual roster
          so the CSV doesn't create duplicates alongside it. */}
      <button
        onClick={onImportInstead}
        className="w-full text-center text-xs font-black text-emerald-400 uppercase tracking-widest py-1"
      >
        Import from GameChanger
      </button>

      {/* Stats destination banner */}
      {roster.length > 0 && hasUsableStats && (
        <div className={cn(
          'rounded-2xl p-4 space-y-1 border',
          statsMode === 'currentSeason'
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'bg-amber-500/10 border-amber-500/30'
        )}>
          <p className={cn(
            'text-[11px] font-black uppercase tracking-widest',
            statsMode === 'currentSeason' ? 'text-emerald-400' : 'text-amber-400'
          )}>
            {statsMode === 'currentSeason' ? 'Tracked as current season' : 'Stored as prior baseline'}
          </p>
          <p className="text-[11px] text-zinc-300 leading-relaxed">
            {statsMode === 'currentSeason'
              ? `Stats will be your live ${parsedSeason} ${parsedYear} totals. New games add to them.`
              : `Stats stored as ${parsedSeason} ${parsedYear} reference. Won't mix with new game stats.`}
          </p>
        </div>
      )}

      {roster.length > 0 && (
        <div className="space-y-2">
          {roster.map(p => {
            const hasBatting = (p.stats?.pa ?? 0) > 0;
            const hasPitching = (p.stats?.ip ?? 0) > 0;
            const isExpanded = expandedId === p.id;
            const ovrValue = calculateOVR(p.attributes.attributes);

            return (
              <div
                key={p.id}
                className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden"
              >
                {/* Collapsed row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : p.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-9 h-9 bg-zinc-800 rounded-lg flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-black text-zinc-400">#{p.number || '–'}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-black uppercase truncate">{p.name}</p>
                        {p.isPitcher && (
                          <span className="text-[8px] font-black text-amber-400 uppercase tracking-widest border border-amber-400/40 bg-amber-500/10 px-1 py-0.5 rounded shrink-0">P</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {p.primaryPosition && (
                          <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            {p.primaryPosition}
                          </span>
                        )}
                        {p.secondaryPosition && (
                          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest bg-zinc-800 px-1.5 py-0.5 rounded">
                            {p.secondaryPosition}
                          </span>
                        )}
                        {(hasBatting || hasPitching) && (
                          <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest truncate">
                            {hasBatting && `${p.stats!.pa}PA`}
                            {hasBatting && hasPitching && ' · '}
                            {hasPitching && `${p.stats!.ip!.toFixed(1)}IP`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <span className="text-sm font-black text-emerald-400">OVR {ovrValue}</span>
                    <ChevronDown className={cn("w-4 h-4 text-zinc-500 transition-transform", isExpanded && "rotate-180")} />
                  </div>
                </button>

                {/* Expanded content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-zinc-800 bg-zinc-950 p-4 space-y-4"
                    >
                      {/* Positions (derived from GC field time, editable) */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Positions</p>
                          {(p.fieldTime && Object.keys(p.fieldTime).length > 0) && (
                            <p className="text-[9px] text-zinc-600 italic">From GameChanger field time</p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest">Primary</label>
                            <select
                              value={p.primaryPosition}
                              onChange={e => onUpdatePositions(p.id, e.target.value, p.secondaryPosition)}
                              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                            >
                              <option value="">—</option>
                              {POSITION_OPTIONS.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest">Secondary</label>
                            <select
                              value={p.secondaryPosition}
                              onChange={e => onUpdatePositions(p.id, p.primaryPosition, e.target.value)}
                              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                            >
                              <option value="">—</option>
                              {POSITION_OPTIONS.filter(pos => pos !== p.primaryPosition).map(pos => <option key={pos} value={pos}>{pos}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Hitting attributes */}
                      <div className="space-y-2">
                        <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Hitting</p>
                        <div className="grid grid-cols-4 gap-2">
                          {['contact', 'power', 'eye', 'approach'].map(attr => (
                            <AttributeChip
                              key={attr}
                              label={attr.substring(0, 3)}
                              value={p.attributes.attributes.hitting[attr as keyof typeof p.attributes.attributes.hitting]}
                              onChange={(newVal) => {
                                const updated = { ...p.attributes };
                                updated.attributes.hitting[attr as keyof typeof updated.attributes.hitting] = newVal;
                                onUpdateAttributes(p.id, updated);
                              }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Speed */}
                      <div className="space-y-2">
                        <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Speed</p>
                        <div className="grid grid-cols-4 gap-2">
                          <AttributeChip
                            label="spd"
                            value={p.attributes.attributes.intangibles.speed}
                            onChange={(newVal) => {
                              const updated = { ...p.attributes };
                              updated.attributes.intangibles.speed = newVal;
                              onUpdateAttributes(p.id, updated);
                            }}
                          />
                        </div>
                      </div>

                      {/* Pitching (only if has pitching stats) */}
                      {hasPitching && (
                        <div className="space-y-2">
                          <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Pitching</p>
                          <div className="grid grid-cols-4 gap-2">
                            {['velocity', 'control'].map(attr => (
                              <AttributeChip
                                key={attr}
                                label={attr.substring(0, 3)}
                                value={p.attributes.attributes.pitching[attr as keyof typeof p.attributes.attributes.pitching]}
                                onChange={(newVal) => {
                                  const updated = { ...p.attributes };
                                  updated.attributes.pitching[attr as keyof typeof updated.attributes.pitching] = newVal;
                                  onUpdateAttributes(p.id, updated);
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Fielding note */}
                      <p className="text-[9px] text-zinc-500 italic">Glove & arm set after first game</p>

                      {/* Remove button */}
                      <button
                        onClick={() => onRemove(p.id)}
                        className="w-full py-2 text-red-400 text-sm font-bold hover:bg-red-500/10 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Remove</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Attribute Chip (inline editor) ────────────────────────────────────────

const AttributeChip = ({
  label, value, onChange,
}: {
  label: string;
  value: number;
  onChange: (val: number) => void;
}) => (
  <div className="flex flex-col items-center space-y-1">
    <div className="text-[10px] font-black text-zinc-400 uppercase">{label}</div>
    <div className="flex items-center space-x-1">
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        className="px-1.5 py-0.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-bold text-zinc-300 transition-colors"
      >
        −
      </button>
      <div className="w-5 h-5 bg-emerald-500/20 rounded flex items-center justify-center">
        <span className="text-xs font-black text-emerald-400">{value}</span>
      </div>
      <button
        onClick={() => onChange(Math.min(5, value + 1))}
        className="px-1.5 py-0.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-bold text-zinc-300 transition-colors"
      >
        +
      </button>
    </div>
  </div>
);

// ─── Main Wizard ──────────────────────────────────────────────────────────────

const ALL_STEPS: Step[] = ['csv', 'statsmode', 'details', 'style', 'agegroup', 'orglevel', 'calendar', 'roster'];

const slideVariants = {
  enter:  (d: number) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:   (d: number) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
};

const generateJoinCode = () =>
  (Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6)).toUpperCase();

export const OnboardingWizard: React.FC<{
  user: any;
  // onComplete fires once team + roster + (optional) games have been written.
  // The optional payload pre-seeds App.tsx state so the coach lands on a
  // populated home tab even if the Firestore snapshot listeners haven't
  // fired yet. openScouting=true means the coach tapped "Scout Your Roster"
  // on the arrival screen — App.tsx opens the scouting session on landing.
  onComplete: (teamId: string, prepopulated?: { games?: any[]; players?: any[] }, options?: { openScouting?: boolean }) => void;
  onCancel?: () => void
}> = ({ user, onComplete, onCancel }) => {
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose');

  // Join-a-team state
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const [step, setStep]     = useState<Step>('csv');
  const [direction, setDir] = useState(1);
  // The animating step layer — see onAnimationComplete below (iOS tap fix).
  const stepLayerRef = useRef<HTMLDivElement | null>(null);

  // CSV
  const [csvParsed, setCsvParsed]   = useState(false);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvError, setCsvError]     = useState<string | null>(null);

  // How to treat the stats from the uploaded CSV. Set in the statsmode step
  // after CSV parse. Default is currentSeason — most coaches uploading mid-
  // season want live tracking. Skipped (defaulted) for coaches who never
  // upload a CSV at all (no stats to route).
  const [statsMode, setStatsMode] = useState<StatsMode>('currentSeason');

  // Optional GameChanger ICS URL. If the coach provides one in the calendar
  // step, handleCreate will fetch + parse + write the games after the team
  // doc is created. Empty string = coach skipped the step.
  const [icsUrl, setIcsUrl] = useState('');
  const [calendarSyncError, setCalendarSyncError] = useState<string | null>(null);

  // Whether the "Manually Confirm League Rules" sheet is open over the
  // orglevel step. The sheet is read-only — it just shows the coach exactly
  // which rules Dugout will enforce. Tapping "Looks Good, Continue →" closes
  // the sheet AND auto-advances the wizard to the next step.
  const [showRulesConfirm, setShowRulesConfirm] = useState(false);

  // Team details
  const [teamName, setTeamName]     = useState('');
  const [season, setSeason]         = useState('Spring');
  const [seasonYear, setSeasonYear] = useState(new Date().getFullYear());
  // Mid-season W-L-T. Only collected when statsMode === 'currentSeason'. Stored
  // as a baseline on the team doc so future game results add to it.
  const [recordW, setRecordW] = useState(0);
  const [recordL, setRecordL] = useState(0);
  const [recordT, setRecordT] = useState(0);

  // Three config axes
  const [styleOfPlay, setStyleOfPlay] = useState<StyleOfPlay | ''>('');
  const [strategyFocus, setStrategyFocus] = useState<'fairness' | 'scouting'>('fairness');
  const [ageGroup, setAgeGroup]       = useState('9U');
  const [org, setOrg]                 = useState('usssa');
  const [level, setLevel]             = useState('AA');

  // Roster
  const [roster, setRoster]     = useState<RosterPlayer[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Arrival moment — a brief celebratory beat shown after the team is written,
  // before we hand off to the home tab. Holds the payload for onComplete so the
  // celebration screen can fire it once the coach taps through (or after a
  // short auto-advance).
  const [arrival, setArrival] = useState<{ teamId: string; payload: { games?: any[]; players?: any[] } } | null>(null);

  // Celebrate the arrival beat — a success buzz as the "You're in, Coach" screen
  // lands (native only; a no-op on web).
  useEffect(() => { if (arrival) celebrate(); }, [arrival]);


  // Steps — orglevel only shown for Players Pitch, statsmode only shown when
  // the coach actually uploaded a CSV (no stats means nothing to route).
  const hasUsableStats = csvParsed && roster.some(p => (p.stats?.pa ?? 0) > 0 || (p.stats?.ip ?? 0) > 0);
  const steps: Step[] = ALL_STEPS.filter(s => {
    if (s === 'orglevel') return styleOfPlay === 'playerspitch';
    if (s === 'statsmode') return hasUsableStats;
    return true;
  });
  const idx    = steps.indexOf(step);
  const isLast = idx === steps.length - 1;

  function navigate(dir: 1 | -1) {
    const next = steps[idx + dir];
    if (!next) return;
    setDir(dir);
    setStep(next);
  }

  function canAdvance(): boolean {
    if (step === 'details') return teamName.trim().length > 0;
    if (step === 'style')   return styleOfPlay !== '';
    return true;
  }

  // ── CSV drop ────────────────────────────────────────────────────────────────

  const onDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    setCsvError(null);
    setCsvParsing(true);

    const parsed = parseGCFilename(file.name);
    if (parsed) {
      setTeamName(parsed.teamName);
      setSeason(parsed.season);
      setSeasonYear(parsed.seasonYear);
    }

    Papa.parse(file, {
      complete: (result) => {
        try {
          const raw = result.data as any[][];
          const players = parseGCCsv(raw);
          if (players.length === 0) {
            setCsvError('No players found. Make sure this is a GameChanger stats export.');
            setCsvParsing(false);
            return;
          }
          setRoster(players.map(p => {
            // Derive positions from GameChanger field time. If the player has
            // no fieldTime data, they'll fall through as a UTIL — but that's
            // the existing behavior, not a regression.
            const derived = derivePositionsFromFieldTime(p.fieldTime);
            const attrs = suggestAttributesFromStats(p.stats);
            const isPitcher = (p.stats?.ip ?? 0) > 0;
            // suggestAttributesFromStats already seeds velocity/control from
            // K/IP and BB/IP. If the player didn't pitch, those stay at 0 and
            // calculateOVR's pitching-bucket exclusion kicks in automatically.
            return {
              id:                makeId(),
              name:              `${p.first} ${p.last}`.trim(),
              number:             p.number,
              stats:              p.stats,
              fieldTime:          p.fieldTime,
              attributes:         attrs,
              primaryPosition:    derived.primaryPosition,
              secondaryPosition:  derived.secondaryPosition,
              positions:          derived.positions,
              isPitcher,
            };
          }));
          setCsvParsed(true);
          setCsvParsing(false);
        } catch {
          setCsvError("Could not read this file. Make sure it's a GameChanger CSV.");
          setCsvParsing(false);
        }
      },
      error: () => { setCsvError('Could not read this file.'); setCsvParsing(false); },
      skipEmptyLines: false,
      header: false,
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'text/plain': ['.csv'] },
    maxFiles: 1,
    disabled: csvParsing,
  });

  // ── Create franchise ────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!user || isSaving) return;
    setIsSaving(true);
    setSaveError(null);

    // Use module-level normalizeOrg helper (also used by LeagueRulesConfirmSheet).
    const normalizedOrg = styleOfPlay === 'playerspitch' ? normalizeOrg(org) : 'independent';

    // Step 1: create the team document AND its join-code lookup doc in one
    // atomic batch. The two must never diverge — if the joinCodes write were
    // separate and failed, the team's printed join code would never resolve
    // and assistant coaches could never join.
    const joinCode = generateJoinCode();
    const teamRef = doc(collection(db, 'teams'));
    try {
      const teamBatch = writeBatch(db);
      teamBatch.set(teamRef, {
      name:         teamName.trim(),
      styleOfPlay:  styleOfPlay || 'playerspitch',
      ageGroup,
      org:          normalizedOrg,
      division:     styleOfPlay === 'playerspitch' ? level : '',
      season,
      seasonYear,
      coachId:      user.uid,
      coaches:      [user.uid],
      joinCode,
      roster:       [],
      games:        [],
      battingOrder: [],
      // Mid-season W-L-T baseline. Only set when the coach is joining
      // mid-season (statsMode === 'currentSeason'). The App.tsx recordString
      // calculation adds these to live game outcomes so a 5-2 team that
      // joins now displays 5-2 immediately and becomes 6-2 after a new W.
      ...(hasUsableStats && statsMode === 'currentSeason' && (recordW > 0 || recordL > 0 || recordT > 0)
        ? { seasonStartRecord: { w: recordW, l: recordL, t: recordT } }
        : {}),
      settings:     {
        pitchCountRuleset: normalizedOrg,
        fieldTimeEqualizerEnabled: strategyFocus === 'fairness',
        onboardingChecklist: {
          // rosterScouted stays false on purpose — the post-wizard Scouting
          // Session is the centerpiece "apply your eye" moment. Wizard-derived
          // attributes are stat-based defaults, not actual scouting.
          rosterScouted: false,
          // calendarSynced gets flipped to true below if the wizard's ICS
          // sync succeeds.
          calendarSynced: false,
          // statsUploaded reflects whether the coach uploaded a GC stats CSV
          // during the wizard — current OR prior baseline both count.
          statsUploaded: hasUsableStats,
          firstGamePlanned: false,
        },
      },
      createdAt:    new Date().toISOString(),
      updatedAt:    new Date().toISOString(),
    });
      // Join code lookup document — ID is the code itself for O(1) lookup.
      teamBatch.set(doc(db, 'joinCodes', joinCode), { teamId: teamRef.id, createdAt: new Date().toISOString() });
      await teamBatch.commit();
      // First stage of the activation funnel. Logged after the commit so it can
      // never count a team that failed to write, and it mirrors the Cloud
      // Function's own `team_created` event so Loops and GA4 agree on the count.
      track('team_created', { styleOfPlay, ageGroup, org, rosterSize: roster.length });
      if (roster.length > 0) track('roster_added', { count: roster.length, source: 'onboarding' });
    } catch (error) {
      console.error('Team creation failed:', error);
      const msg = error instanceof Error ? error.message : String(error);
      setSaveError(`Team: ${msg}`);
      setIsSaving(false);
      return;
    }

    // Step 2: batch-write players into the new team's subcollection. We also
    // collect each player's doc with its Firestore ID so we can hand the
    // canonical roster straight to App.tsx via onComplete — same trick as the
    // games seed below. The players snapshot listener can lag for a beat after
    // a fresh batch write and we don't want the coach to land on a "0 players"
    // dashboard until they refresh.
    const seededPlayers: any[] = [];
    try {
      const batch = writeBatch(db);
      const playersCollection = collection(db, `teams/${teamRef.id}/players`);
      for (const p of roster) {
        const playerDoc = doc(playersCollection);

        // The stats destination depends on the coach's choice in the
        // statsmode step. Current season → live `stats` field. Prior season →
        // new `priorSeasonStats` field that won't be touched by new games.
        const statsFields = p.stats
          ? (statsMode === 'currentSeason'
              ? { stats: p.stats }
              : { priorSeasonStats: { season, year: seasonYear, stats: p.stats, ...(p.fieldTime ? { fieldTime: p.fieldTime } : {}) } })
          : {};

        const playerData = {
          name:    p.name,
          number:  p.number,
          attributes:        p.attributes.attributes,
          positions:         p.positions,
          primaryPosition:   p.primaryPosition,
          secondaryPosition: p.secondaryPosition,
          ovr:               calculateOVR(p.attributes.attributes, p.primaryPosition),
          ...statsFields,
          teamId:  teamRef.id,
          coachId: user.uid,
        };
        batch.set(playerDoc, playerData);
        seededPlayers.push({ id: playerDoc.id, ...playerData });
      }
      await batch.commit();
    } catch (error) {
      console.error('Player batch failed:', error);
      const msg = error instanceof Error ? error.message : String(error);
      setSaveError(`Players: ${msg}`);
      setIsSaving(false);
      return;
    }

    // Step 2b: if the coach imported CURRENT-season stats via CSV, season
    // aggregation (buildSeasonStats) only ever reads from game docs — it
    // never looks at player.stats directly. So a season CSV with no game to
    // attach to would otherwise sit on the player doc and never surface in
    // the Stats tab. We synthesize one completed "game" doc holding all of
    // it, keyed by the real player IDs from the batch above, so it flows
    // through the exact same path as a real per-game GC import.
    // `source: 'onboardingImport'` marks it so App.tsx can hide it from the
    // Games tab (it's not a game the coach played) while useSeasonStats
    // still picks it up. Prior-season baselines are intentionally excluded —
    // that mode already isolates itself into priorSeasonStats above.
    let importedStatsGame: any = null;
    if (statsMode === 'currentSeason') {
      const stripUndefined = (obj: Record<string, any>): Record<string, any> =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
      const statsByPlayer: Record<string, any> = {};
      for (const p of seededPlayers) {
        if (p.stats) statsByPlayer[p.id] = stripUndefined(p.stats);
      }
      if (Object.keys(statsByPlayer).length > 0) {
        try {
          const gamesCollection = collection(db, `teams/${teamRef.id}/games`);
          const gameDoc = doc(gamesCollection);
          const gameData = {
            opponent: 'Season stats import',
            date: new Date().toISOString(),
            isHome: true,
            status: 'completed',
            season,
            seasonYear,
            source: 'onboardingImport',
            stats: statsByPlayer,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          await setDoc(gameDoc, gameData);
          importedStatsGame = { id: gameDoc.id, ...gameData };
        } catch (error) {
          // Non-fatal — the coach still gets their team and roster; stats
          // just won't show in the Stats tab until they re-import.
          console.error('Onboarding stats-game synthesis failed:', error);
        }
      }
    }

    // Step 3: optional ICS calendar import. If the coach pasted a URL in the
    // calendar step, fetch the feed, parse out games, and write them into the
    // team's games subcollection. Non-fatal — if it fails we still proceed.
    // We also collect the parsed games with their Firestore IDs so we can hand
    // them straight to App.tsx via onComplete — the Firestore snapshot listener
    // can lag a beat after a fresh write, and we don't want the coach to land
    // on an empty Games tab just because the listener hasn't fired yet.
    const syncedGames: any[] = [];
    if (importedStatsGame) syncedGames.push(importedStatsGame);
    const trimmedIcs = icsUrl.trim();
    if (trimmedIcs) {
      try {
        const response = await fetchProxiedIcs(trimmedIcs);
        if (response.ok) {
          const text = await response.text();
          const games = parseICS(text, teamName.trim());
          if (games.length > 0) {
            const gamesBatch = writeBatch(db);
            const gamesCollection = collection(db, `teams/${teamRef.id}/games`);
            for (const g of games) {
              const gameDoc = doc(gamesCollection);
              gamesBatch.set(gameDoc, g);
              syncedGames.push({ id: gameDoc.id, ...g });
            }
            await gamesBatch.commit();
            // Mark the checklist item complete so the Home tab checklist
            // doesn't ask the coach to do something they already did.
            await updateDoc(doc(db, 'teams', teamRef.id), {
              'settings.onboardingChecklist.calendarSynced': true,
            });
          }
        }
      } catch (err) {
        // Surface in the UI but don't block team creation — the coach can
        // sync later from the Home checklist.
        console.error('Calendar sync failed during onboarding:', err);
        setCalendarSyncError('We created your team, but the calendar sync failed. Try it again from Settings.');
      }
    }

    // Hand off to the arrival moment instead of jumping straight to Home — a
    // brief celebration before the coach lands. onComplete fires from there.
    setIsSaving(false);
    setArrival({ teamId: teamRef.id, payload: { games: syncedGames, players: seededPlayers } });
  };

  // ── Join a team via code ────────────────────────────────────────────────────

  const handleJoin = async () => {
    const code = joinCodeInput.trim().toUpperCase();
    if (!code || isJoining) return;
    setIsJoining(true);
    setJoinError(null);
    try {
      const codeDoc = await getDoc(doc(db, 'joinCodes', code));
      if (!codeDoc.exists()) {
        setJoinError('Invalid join code. Double-check and try again.');
        return;
      }
      const { teamId } = codeDoc.data();
      await updateDoc(doc(db, 'teams', teamId), {
        coaches: arrayUnion(user.uid),
      });
      onComplete(teamId);
    } catch (err: any) {
      setJoinError(err.message ?? 'Something went wrong. Try again.');
    } finally {
      setIsJoining(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  function renderStep() {
    switch (step) {
      case 'csv':
        return (
          <CsvStep
            csvParsed={csvParsed} csvParsing={csvParsing} csvError={csvError}
            rosterCount={roster.length}
            pitcherCount={roster.filter(p => p.isPitcher).length}
            teamName={teamName} season={season} seasonYear={seasonYear}
            hasStats={roster.some(p => (p.stats?.pa ?? 0) > 0 || (p.stats?.ip ?? 0) > 0)}
            getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive}
            onNext={() => navigate(1)} onSkip={() => navigate(1)}
          />
        );
      case 'statsmode':
        return (
          <StatsModeStep
            statsMode={statsMode}
            setStatsMode={setStatsMode}
            parsedSeason={season}
            parsedYear={seasonYear}
            playerCount={roster.length}
          />
        );
      case 'details':
        return (
          <DetailsStep
            teamName={teamName} setTeamName={setTeamName}
            season={season} setSeason={setSeason}
            seasonYear={seasonYear} setSeasonYear={setSeasonYear}
            showRecord={hasUsableStats && statsMode === 'currentSeason'}
            recordW={recordW} setRecordW={setRecordW}
            recordL={recordL} setRecordL={setRecordL}
            recordT={recordT} setRecordT={setRecordT}
          />
        );
      case 'style':
        return (
          <StyleStep 
            styleOfPlay={styleOfPlay} 
            setStyleOfPlay={setStyleOfPlay} 
            strategyFocus={strategyFocus}
            setStrategyFocus={setStrategyFocus}
          />
        );
      case 'agegroup':
        return <AgeGroupStep ageGroup={ageGroup} setAgeGroup={setAgeGroup} />;
      case 'orglevel':
        return (
          <OrgLevelStep
            org={org}
            setOrg={(v) => { setOrg(v); setLevel(ORGS.find(o => o.value === v)?.levels[0] ?? 'AA'); }}
            level={level}
            setLevel={setLevel}
            ageGroup={ageGroup}
            onConfirmRules={() => setShowRulesConfirm(true)}
          />
        );
      case 'calendar':
        return <CalendarStep icsUrl={icsUrl} setIcsUrl={setIcsUrl} />;
      case 'roster':
        return (
          <RosterStep
            roster={roster}
            onAddPlayer={(p) => setRoster(r => [...r, p])}
            onRemove={(id) => setRoster(r => r.filter(p => p.id !== id))}
            onUpdateAttributes={(id, attributes) => setRoster(r => r.map(p => p.id === id ? { ...p, attributes } : p))}
            onUpdatePositions={(id, primary, secondary) => setRoster(r => r.map(p => p.id === id ? { ...p, primaryPosition: primary, secondaryPosition: secondary } : p))}
            statsMode={statsMode}
            parsedSeason={season}
            parsedYear={seasonYear}
            hasUsableStats={hasUsableStats}
            onImportInstead={() => {
              // Clear manually-typed players so the CSV import doesn't create
              // duplicates alongside them, then jump back to the CSV drop zone.
              setRoster([]);
              setCsvParsed(false);
              setCsvError(null);
              setDir(-1);
              setStep('csv');
            }}
          />
        );
    }
  }

  // ── Arrival moment ────────────────────────────────────────────────────────────
  // Shown once the team is written, before handing off to Home. Marks the
  // transition with a celebratory beat instead of dropping the coach onto a
  // quiet home tab.
  if (arrival) {
    const playerCount = arrival.payload.players?.length ?? 0;
    const gameCount = arrival.payload.games?.length ?? 0;
    const finish = () => onComplete(arrival.teamId, arrival.payload);
    // The fastest path to the wow: with a roster in hand, the primary CTA
    // drops the coach straight into the scouting session (live OVR preview,
    // ranked reveal) instead of a quiet home tab.
    const finishAndScout = () => onComplete(arrival.teamId, arrival.payload, { openScouting: true });
    return (
      <div className="legacy-invert min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-8 text-center">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 18 }}
          className="w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center mb-7 shadow-[0_0_50px_rgba(16,185,129,0.4)]"
        >
          <Check className="w-11 h-11 text-black" />
        </motion.div>
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-400 mb-2">You're in, Coach</p>
          <h1 className="text-4xl font-black italic uppercase tracking-tight leading-none mb-3">
            {teamName || 'Your Team'}<br />is ready.
          </h1>
          <p className="text-zinc-400 text-sm mb-8">
            {playerCount > 0 ? `${playerCount} player${playerCount === 1 ? '' : 's'} on the roster` : 'Time to build your roster'}
            {gameCount > 0 ? ` · ${gameCount} game${gameCount === 1 ? '' : 's'} on the schedule` : ''}.
          </p>
        </motion.div>
        {playerCount > 0 ? (
          <motion.div
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="w-full max-w-sm space-y-3"
          >
            <button
              onClick={finishAndScout}
              className="w-full py-4 bg-emerald-500 text-black font-black rounded-2xl uppercase italic text-lg shadow-[0_0_30px_rgba(16,185,129,0.25)]"
            >
              Scout Your Roster →
            </button>
            <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-widest">
              5 minutes. Every player gets a rating.
            </p>
            <button
              onClick={finish}
              className="w-full py-3 text-sm font-bold text-zinc-400 hover:text-white transition-colors"
            >
              I'll do it later
            </button>
          </motion.div>
        ) : (
          <motion.button
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            onClick={finish}
            className="w-full max-w-sm py-4 bg-emerald-500 text-black font-black rounded-2xl uppercase italic text-lg shadow-[0_0_30px_rgba(16,185,129,0.25)]"
          >
            Let's Coach →
          </motion.button>
        )}
      </div>
    );
  }

  // ── Choose screen ────────────────────────────────────────────────────────────
  if (mode === 'choose') {
    return (
      <div className="legacy-invert min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-8">
        {onCancel && (
          <button
            onClick={onCancel}
            className="absolute left-6 flex items-center gap-1.5 text-xs font-black uppercase text-zinc-500 hover:text-white transition-colors"
            style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}
        <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(16,185,129,0.3)]">
          <Users className="w-9 h-9 text-black" />
        </div>
        <h1 className="text-3xl font-black italic uppercase tracking-tighter mb-2">Welcome to Dugout</h1>
        <p className="text-zinc-500 text-sm font-medium mb-10">Are you starting a new team or joining one?</p>
        <div className="w-full max-w-sm space-y-3">
          <button
            onClick={() => setMode('create')}
            className="w-full py-5 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm uppercase italic tracking-widest rounded-2xl transition-colors shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          >
            Create a New Team
          </button>
          <button
            onClick={() => setMode('join')}
            className="w-full py-5 bg-zinc-800 hover:bg-zinc-700 text-white font-black text-sm uppercase italic tracking-widest rounded-2xl transition-colors border border-zinc-700"
          >
            Join a Team with Code
          </button>
        </div>
      </div>
    );
  }

  // ── Join screen ───────────────────────────────────────────────────────────────
  if (mode === 'join') {
    return (
      <div className="legacy-invert min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-8">
        <button
          onClick={() => { setMode('choose'); setJoinError(null); setJoinCodeInput(''); }}
          className="absolute top-6 left-4 p-2 rounded-xl text-zinc-500 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mb-6 border border-zinc-700">
          <KeyRound className="w-9 h-9 text-emerald-400" />
        </div>
        <h1 className="text-2xl font-black italic uppercase tracking-tighter mb-2">Join a Team</h1>
        <p className="text-zinc-500 text-sm font-medium mb-8 text-center">Enter the join code your head coach shared with you.</p>
        <div className="w-full max-w-sm space-y-3">
          <input
            type="text"
            value={joinCodeInput}
            onChange={e => setJoinCodeInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleJoin()}
            placeholder="e.g. KC9U4F2X"
            maxLength={8}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl px-5 py-4 text-white font-black text-xl text-center uppercase tracking-widest outline-none focus:border-emerald-500 transition-colors placeholder:text-zinc-600"
          />
          {joinError && (
            <p className="text-red-400 text-xs font-bold text-center uppercase tracking-widest">{joinError}</p>
          )}
          <button
            onClick={handleJoin}
            disabled={joinCodeInput.trim().length < 6 || isJoining}
            className="w-full py-5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black text-sm uppercase italic tracking-widest rounded-2xl transition-colors"
          >
            {isJoining ? 'Joining…' : 'Join Team'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="legacy-invert min-h-screen bg-zinc-950 text-white flex flex-col overflow-hidden">
      {/* Header — safe-area inset so the back chevron clears the status-bar clock
          on notched iPhones (was pt-6 only, which tucked it under the clock). */}
      <div
        className="flex items-center justify-between px-4 pb-2 shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}
      >
        <button
          onClick={() => idx === 0 ? setMode('choose') : navigate(-1)}
          className="p-2 rounded-xl text-zinc-500 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center space-x-1.5">
          {steps.map((s, i) => (
            <div
              key={s}
              className={cn(
                'rounded-full transition-all duration-300',
                i === idx ? 'w-5 h-1.5 bg-emerald-500'
                : i < idx ? 'w-1.5 h-1.5 bg-emerald-500/40'
                           : 'w-1.5 h-1.5 bg-zinc-800'
              )}
            />
          ))}
        </div>

        <div className="w-9" />
      </div>

      {/* Step content */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.27, ease: [0.25, 0.46, 0.45, 0.94] }}
            // iOS WKWebView drops taps on a transformed/animating layer: the
            // slide leaves an inline `transform` whose compositing layer swallows
            // the first touches on inner controls (Season buttons, Year select).
            // Once the slide settles, strip the inline transform so the layer
            // hit-tests normally. (Desktop pointer events don't hit this; iOS
            // touch does — which is why Fall taps did nothing on device.)
            onAnimationComplete={(def) => {
              if (def === 'center' && stepLayerRef.current) {
                stepLayerRef.current.style.transform = '';
                stepLayerRef.current.style.willChange = 'auto';
              }
            }}
            ref={stepLayerRef}
            className="absolute inset-0 overflow-y-auto"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer — CSV step manages its own CTAs */}
      {step !== 'csv' && (
        <div className="p-6 shrink-0 space-y-2">
          {saveError && (
            <p className="text-xs text-red-400 text-center font-bold pb-1">{saveError}</p>
          )}
          {calendarSyncError && (
            <p className="text-xs text-amber-400 text-center font-bold pb-1">{calendarSyncError}</p>
          )}
          <button
            onClick={isLast ? handleCreate : () => navigate(1)}
            disabled={!canAdvance() || isSaving}
            className="w-full py-4 bg-emerald-500 text-black font-black rounded-2xl uppercase italic text-lg shadow-[0_0_30px_rgba(16,185,129,0.2)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLast
              ? (isSaving ? 'Creating Franchise...' : 'Create Franchise')
              : 'Next →'}
          </button>
        </div>
      )}

      {/* Confirm League Rules sheet — opens over the orglevel step when the
          coach taps "Manually Confirm League Rules". Read-only view of the
          rules Dugout will enforce. "Looks Good, Continue →" closes the
          sheet AND advances the wizard to the next step. */}
      <AnimatePresence>
        {showRulesConfirm && (
          <LeagueRulesConfirmSheet
            org={org}
            level={level}
            ageGroup={ageGroup}
            onClose={() => setShowRulesConfirm(false)}
            onContinue={() => {
              setShowRulesConfirm(false);
              navigate(1);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
